// Display-only formatting for dose combination strings.
//
// HARD CONSTRAINT: this module is presentation-layer only. A combination string
// does double duty — it is both the human label AND the validated key that the
// backend and the concordance math parse verbatim (see dose-utils.ts). Nothing
// here may ever feed back into a stored or submitted value: callers render the
// label and keep passing the original string through onChange untouched.
//
// The daily average comes from parseDoseCombination rather than a local
// re-implementation, so the number shown to the clinician is by construction the
// same number the concordance tiers are computed from.

import { parseDoseCombination } from '@/components/dose-utils';
import { NO_TREATMENT_DOSE } from './doseOptions';

export interface DoseLabel {
  /** Schedule-first label, e.g. "88 mcg ×6 + 100 mcg ×1 / εβδ." */
  primary: string;
  /** Daily average, e.g. "≈ 89,7 mcg/ημ." Omitted when redundant or meaningless. */
  secondary?: string;
  /** Daily average as a number — drives ordering and numeric search. */
  dailyAverage: number;
}

interface Segment {
  days: number;
  /** Display form of the strength: "88", or "200+25" for combined tablets. */
  strength: string;
  /** True for the "N x (A+B)" form — two tablets taken together, same day. */
  combined: boolean;
}

/** Matches "N x D" and "N x (A+B)", in source order. */
const SEGMENT_RE = /(\d+)\s*x\s*(?:\((\d+)\+(\d+)\)|(\d+))/g;

const parseSegments = (combination: string): Segment[] => {
  const segments: Segment[] = [];
  SEGMENT_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SEGMENT_RE.exec(combination)) !== null) {
    const [, days, parenA, parenB, simple] = match;
    segments.push(
      simple !== undefined
        ? { days: Number(days), strength: simple, combined: false }
        : { days: Number(days), strength: `${parenA}+${parenB}`, combined: true },
    );
  }
  return segments;
};

/** Greek decimal comma; a trailing ",0" is dropped. */
const formatNumber = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
};

// "μαζί" mirrors the clinical wording in the backend config's own explanation
// for this notation: "τη δόση των 200 μg συν τη δόση των 25μg μαζί (ταυτόχρονα)".
const formatSegment = (seg: Segment): string =>
  seg.combined ? `${seg.strength} μαζί` : `${seg.strength} mcg`;

/**
 * Render a validated combination string as a clinician-readable label.
 * Schedule leads (what the patient takes); the daily average is secondary.
 *
 * Segments are ordered by day-count descending so the dose taken on most days
 * reads first. This is a display reordering only — the input string is never
 * rewritten.
 */
export const formatDose = (combination: string): DoseLabel => {
  if (!combination) return { primary: '', dailyAverage: 0 };

  if (combination === NO_TREATMENT_DOSE) {
    return { primary: 'Χωρίς αγωγή (ενεργός παρακολούθηση)', dailyAverage: 0 };
  }

  const segments = parseSegments(combination);
  const { dailyAverage } = parseDoseCombination(combination);

  // Unrecognised shape: fall back to the raw string rather than showing nothing.
  if (segments.length === 0) return { primary: combination, dailyAverage };

  const ordered = [...segments].sort((a, b) => b.days - a.days);

  let primary: string;
  let secondary: string | undefined = `≈ ${formatNumber(dailyAverage)} mcg/ημ.`;

  if (ordered.length === 1) {
    const [only] = ordered;
    if (only.days === 7) {
      primary = only.combined
        ? `${only.strength} mcg μαζί, καθημερινά`
        : `${only.strength} mcg καθημερινά`;
      // For a plain 7-day dose the daily average just restates the strength.
      if (!only.combined) secondary = undefined;
    } else {
      primary = `${formatSegment(only)} ×${only.days} / εβδ.`;
    }
  } else {
    primary = `${ordered.map((s) => `${formatSegment(s)} ×${s.days}`).join(' + ')} / εβδ.`;
  }

  return { primary, secondary, dailyAverage };
};

/**
 * Search haystack for a dose: the raw string (both spaced and unspaced, so
 * "7x50" matches "7 x 50" as the input placeholder has always advertised) plus
 * the rendered label, so a clinician can type either notation.
 */
export const doseSearchText = (combination: string): string => {
  const { primary, secondary } = formatDose(combination);
  return [combination, combination.replace(/\s+/g, ''), primary, secondary ?? '']
    .join(' ')
    .toLowerCase();
};

export interface DoseGroup {
  heading: string;
  doses: string[];
}

const BAND_WIDTH = 25;

/**
 * Order combinations by daily average and split them into 25 mcg/day bands.
 *
 * The source list is authored in construction order — runs of adjacent tablet
 * strengths, with four "skip-tier" blocks (50↔75, 75↔100, 100↔125, 125↔150)
 * appended after the runs they overlap. That produces four ~19,6 mcg/day
 * resets, so any given dose range appears in two places in the list. Sorting
 * collapses it into one ascending ramp.
 *
 * Ties break toward the simpler regimen (fewer distinct strengths), matching
 * the backend config's own lookup_rules.preference_hierarchy, which prefers
 * single_dose over simple_alternating.
 */
export const groupCombinationDoses = (doses: string[]): DoseGroup[] => {
  const decorated = doses.map((dose, index) => ({
    dose,
    index,
    daily: parseDoseCombination(dose).dailyAverage,
    complexity: parseSegments(dose).length,
  }));

  decorated.sort(
    (a, b) => a.daily - b.daily || a.complexity - b.complexity || a.index - b.index,
  );

  const groups: DoseGroup[] = [];
  let currentBand = -1;

  for (const entry of decorated) {
    const band = Math.floor(entry.daily / BAND_WIDTH);
    if (band !== currentBand) {
      currentBand = band;
      const low = band * BAND_WIDTH;
      groups.push({
        heading: band === 0 ? 'έως 25 mcg/ημ.' : `≈ ${low}–${low + BAND_WIDTH} mcg/ημ.`,
        doses: [],
      });
    }
    groups[groups.length - 1].doses.push(entry.dose);
  }

  return groups;
};
